/// <amd-module name="VSSUI/Components/Breadcrumb/Breadcrumb" />

import * as React from "react";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IMenuItem, IMenuItemDetails, MenuCell } from "VSSUI/Menu";
import { OverflowButton, ResizeGroup } from "VSSUI/ResizeGroup";
import * as Resources from "VSSUI/Resources.Breadcrumb";
import { IResponsiveLayoutProps } from "VSSUI/ResponsiveLayout";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, getSafeId } from "VSSUI/Util";
import { IBreadcrumbItem, IBreadcrumbProps } from "./Breadcrumb.Props";

export interface IBreadcrumbState {
    displayedItems: IBreadcrumbItem[];
    linkItems: IBreadcrumbItem[];
    hiddenItemCount: number;
}

let breadcrumbCount = 0;

export class Breadcrumb extends React.Component<IBreadcrumbProps, IBreadcrumbState> {
    public static defaultProps: IBreadcrumbProps = {
        items: []
    };

    public static getDerivedStateFromProps(props: IBreadcrumbProps, state: IBreadcrumbState): IBreadcrumbState {
        const displayedItems = arrangeItems(props.items);
        return { ...state, displayedItems, linkItems: getLinkItems(displayedItems) };
    }
    private breadcrumbId: number;

    constructor(props: IBreadcrumbProps) {
        super(props);

        this.breadcrumbId = breadcrumbCount++;
        this.state = Breadcrumb.getDerivedStateFromProps(props, this.state);
    }

    public render() {
        const items = this.state.displayedItems;
        const overflowMenuItems: IMenuItem[] = items.map(
            (item, index) =>
                ({
                    data: item,
                    iconProps: item.iconProps,
                    text: item.text,
                    id: item.key,
                    href: item.href,
                    ...item.menuItemProps,
                    onActivate: this.onMenuItemActivate
                } as IMenuItem)
        );

        if (this.props.extraContent) {
            overflowMenuItems.push({
                id: "overflow-item",
                className: css(this.props.extraContentClassName || ""),
                onActivate: () => {
                    return true;
                },
                renderMenuCell: (menuCell: MenuCell, menuItem: IMenuItem, details: IMenuItemDetails) => {
                    if (menuCell === MenuCell.PrimaryText) {
                        return this.props.extraContent;
                    }
                }
            } as IMenuItem);
        }
        const responsiveChildren = overflowMenuItems.map((items, index) => index + 1);
        const responsiveLayoutProps: IResponsiveLayoutProps = { responsiveChildren, onLayoutChange: this.onLayoutChanged };

        return (
            <div className={css("bolt-breadcrumb-container", items.length > 0 && "bolt-breadcrumb-with-items", this.props.className)}>
                <ResizeGroup responsiveLayoutProps={responsiveLayoutProps} overflowMenuItems={overflowMenuItems}>
                    {this.renderBreadcrumbItems()}
                </ResizeGroup>
            </div>
        );
    }

    private renderBreadcrumbItems = () => {
        const { excludeFocusZone, excludeTabStop, extraContent, role } = this.props;
        const { displayedItems, hiddenItemCount } = this.state;
        const ariaLabel = this.props.ariaLabel || Resources.BreadcrumbItemAriaLabel;

        // Find index of last rendered item so the divider icon
        // knows not to render on that item
        const lastItemIndex = displayedItems.length - 1;

        const itemElements: JSX.Element[] = displayedItems.map((item, index) => (
            <div className="bolt-breadcrumb-list-item" key={item.key || String(index)}>
                {this.renderItem(item)}
                {(index !== lastItemIndex || extraContent) && this.renderDivider(item.key)}
            </div>
        ));

        itemElements.splice(
            0,
            0,
            <div className={css("bolt-breadcrumb-overflow", hiddenItemCount > 0 && "bolt-breadcrumb-overflow-visible")} key="overflowItem">
                <OverflowButton excludeTabStop={excludeTabStop} excludeFocusZone={excludeFocusZone} />
                {hiddenItemCount > 0 && hiddenItemCount < displayedItems.length && this.renderDivider("overflow")}
            </div>
        );

        if (extraContent) {
            itemElements.push(
                <div className="bolt-breadcrumb-list-item" id={getSafeId("breadcrumb-extra-content")} key="breadcrumb-extra-content">
                    {extraContent}
                </div>
            );
        }

        return (
            <div
                className={css("bolt-breadcrumb flex-row flex-grow", itemElements.length > 0 && "bolt-breadcrumb-with-items")}
                role={role}
                aria-label={ariaLabel}
            >
                {...itemElements}
            </div>
        );
    };

    private renderDivider = (key: string): JSX.Element => {
        return (
            <div key={"BID$" + key} className="bolt-breadcrumb-divider secondary-text cursor-default flex-noshrink">
                /
            </div>
        );
    };

    private renderItem = (item: IBreadcrumbItem): JSX.Element | null => {
        const internalItem = (
            <>
                {item.iconProps && (
                    <Icon {...item.iconProps} className={css("bolt-breadcrumb-item-icon", !item.text && "icon-only", item.iconProps.className)} />
                )}
                {item.text && (
                    <Tooltip overflowOnly={true}>
                        <span className="bolt-breadcrumb-item-text text-ellipsis">{item.text}</span>
                    </Tooltip>
                )}
            </>
        );

        const ariaDescribedById = "bolt-breadcrumb" + this.breadcrumbId + "-item-described-by" + item.key;

        if (item.onClick || item.href) {
            return (
                <div className="bolt-breadcrumb-item">
                    <Link
                        ariaLabel={item.ariaLabel}
                        ariaDescribedBy={ariaDescribedById}
                        key={"BreadcrumbItem$" + item.key}
                        onClick={item.onClick as any}
                        href={item.href}
                        excludeFocusZone={this.props.excludeFocusZone}
                        excludeTabStop={this.props.excludeTabStop}
                        removeUnderline={true}
                    >
                        <div className="bolt-breadcrumb-item-text-container">{internalItem}</div>
                        <div className="bolt-breadcrumb-hidden-element" id={getSafeId(ariaDescribedById)}>
                            {item.ariaDescription}
                        </div>
                    </Link>
                </div>
            );
        } else {
            return (
                <div className="bolt-breadcrumb-item" aria-label={item.ariaLabel}>
                    {internalItem}
                </div>
            );
        }
    };

    private onLayoutChanged = (hiddenCount: number) => {
        this.setState({ hiddenItemCount: hiddenCount });
    };

    private onMenuItemActivate = (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        const breadcrumbItem = menuItem.data as IBreadcrumbItem;
        if (breadcrumbItem.onClick) {
            breadcrumbItem.onClick(event as React.MouseEvent<HTMLElement>, breadcrumbItem);
        }
    };
}

function getLinkItems(displayedItems: IBreadcrumbItem[]): IBreadcrumbItem[] {
    return displayedItems.filter(item => {
        return item.href || item.onClick;
    });
}

function arrangeItems(items?: IBreadcrumbItem[]): IBreadcrumbItem[] {
    if (items === undefined) {
        return [];
    }
    // Build map of what is currently in the list of breadcrumb items
    const existingItemsMap: { [id: string]: IBreadcrumbItem } = {};
    // If exisiting items have the same id and different priorities, the higher priority item will be shown.
    items.forEach(item => {
        if (!existingItemsMap[item.key] || (existingItemsMap[item.key].priority || 0) <= (item.priority || 0)) {
            existingItemsMap[item.key] = item;
        }
    });

    const newItems: IBreadcrumbItem[] = [];
    Object.keys(existingItemsMap).forEach(key => {
        if (!existingItemsMap[key].hidden) {
            newItems.push(existingItemsMap[key]);
        }
    });

    const shouldSortItems = newItems.some(x => x.rank! >= 0);

    if (shouldSortItems) {
        // This is not a stable sort. If we pass items with the same rank, we may not always get them back in the
        // same order. We have a stable sort in vssf webplatform, but we're not taking a dependency there.
        newItems.sort((a, b) => {
            const aRank = a.rank || Number.MAX_VALUE;
            const bRank = b.rank || Number.MAX_VALUE;
            return aRank - bRank;
        });
    }

    return newItems;
}
